import LicensePlate from '@/components/vehicle/LicensePlate/LicensePlate'
import styles from './WheelSearch.module.scss'
import React, { useCallback, useEffect, useMemo } from 'react'
import Button from '@/components/buttons/Button/Button'
import VehicleSelector from '@/components/vehicle/VehicleSelector/VehicleSelector'
import { useTranslation } from 'next-i18next'
import { useLazyGetRimsQuery } from '@/api/rims'
import { useLazyGetRimTyresQuery } from '@/api/tyres'
import SearchResult from '../SearchResult/SearchResult'
import { useAppDispatch } from '@/utils/hooks/useAppDispatch'
import { ArticleModel, RimSearchCriteraModel, RimTyresSearchModel } from '@/types/api'
import { resetVehicle, selectLicensePlate, selectLicensePlateCountryCode, selectLicensePlateValid } from '@/features/vehicle/vehicleSlice'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import useAddToCart from '@/utils/hooks/useAddToCart'
import RimFilter from '@/components/filter/RimFilter/RimFilter'
import { useRouter } from 'next/router'
import NoCarFound from '@/components/common/NoCarFound/NoCarFound'
import WheelTyreFilter from '@/components/filter/WheelTyreFilter/WheelTyreFilter'
import WheelSearchStep from '@/types/WheelSearchStep'
import ProductCardSimple from '@/components/common/ProductCardSimple/ProductCardSimple'
import VehicleInfo from '@/components/vehicle/VehicleInfo/VehicleInfo'
import { useLazyGetArticleQuery } from '@/api/article'
import useUpdateQuery from '@/utils/hooks/useUpdateQuery'
import { filterObject, stringToNumber } from '@/utils/helpers'
import WheelSearchQueryParams from '@/types/WheelSearchQueryParams'
import { usePrevious } from '@/utils/hooks/usePrevious'
import FilterChangeHandler from '@/types/FilterChangeHandler'

type Props = {}

interface PageQueryParams extends WheelSearchQueryParams {
  searchplate?: string;
}

const WheelSearch = (props: Props) => {
  const { i18n, t } = useTranslation()
  const [triggerRims, rimResult] = useLazyGetRimsQuery()
  const [triggerTyres, tyreResult] = useLazyGetRimTyresQuery()
  const [triggerArticle, _] = useLazyGetArticleQuery()
  const [filteredRims, setFilterdRims] = React.useState<any[]>()
  const [filteredTyres, setFilterdTyres] = React.useState<any[]>()
  const queryLoaded = React.useRef<boolean>(false)
  const licensePlate = useAppSelector(selectLicensePlate)
  const licensePlateValid = useAppSelector(selectLicensePlateValid)
  const licensePlateCountryCode = useAppSelector(selectLicensePlateCountryCode)
  const [selectedRim, setSelectedRim] = React.useState<ArticleModel | undefined>(undefined)
  const [step, setStep] = React.useState<WheelSearchStep>(WheelSearchStep.Search)
  const prevStep = usePrevious(step)
  const [rimCriteria, setRimsCriteria] = React.useState<RimSearchCriteraModel | undefined>(undefined)
  const [tyresCriteria, setTyresCriteria] = React.useState<RimTyresSearchModel | undefined>(undefined)
  const rimFilterState = React.useRef<string>('')
  const tyreFilterState = React.useRef<string>('')
  const { addPackageToCart } = useAddToCart()
  const { query, updateQuery, resetQuery } = useUpdateQuery<WheelSearchQueryParams>()
  const router = useRouter()
  const dispatch = useAppDispatch()

  /**
   * Constructs and returns an initial query structure based on the current state.
   *
   * @returns {WheelSearchQueryParams} - An object structured as BasePageQueryParams with values derived from the current state.
   */
  const buildQuery = useCallback((criteria: RimSearchCriteraModel): WheelSearchQueryParams => {
    const params: WheelSearchQueryParams = {
      licenseplate: criteria?.licensePlate || '',
      licensecountrycode: criteria?.licenseCountryCode?.toString() || '',
      ktype: criteria?.kTypeId?.toString() || rimResult.data?.carInfo.kTypeId.toString() || '',
      rims: selectedRim ? selectedRim.id.toString() : '',
      pid: query.pid || '',
    }

    return filterObject(params)
  }, [query.pid, rimResult.data?.carInfo.kTypeId, selectedRim])

  const rebuildQuery = useCallback((criteria: RimSearchCriteraModel, overwriteQuery?: boolean) => {
    let query = buildQuery(criteria)
    updateQuery(query, overwriteQuery)
  }, [buildQuery, updateQuery])

  const triggerSearch = useCallback((criteria: RimSearchCriteraModel, overwriteQuery?: boolean) => {
    setFilterdRims(undefined)
    setFilterdTyres(undefined)
    triggerRims(criteria)
    rebuildQuery(criteria, overwriteQuery)
    setStep(WheelSearchStep.Rim)
    setRimsCriteria(criteria)
    setSelectedRim(undefined)
  }, [rebuildQuery, triggerRims])

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    triggerLicensePlateSearch()
  }

  const triggerLicensePlateSearch = () => {
    if (licensePlateValid) {
      triggerSearch({
        description: '',
        languageCode: i18n.language,
        licensePlate: licensePlate || '',
        licenseCountryCode: licensePlateCountryCode,
      }, true)
    }
  }

  const resetCriteria = () => {
    setStep(WheelSearchStep.Search)
    setRimsCriteria(undefined)
    dispatch(resetVehicle())
    resetQuery()
    rimFilterState.current = ''
    tyreFilterState.current = ''
  }

  const handleOnSelectVehicle = (kTypeId: number) => {
    triggerSearch({
      kTypeId,
      licensePlate: '',
      description: '',
      languageCode: i18n.language,
      licenseCountryCode: ''
    }, true)
  }

  const onFilterChange: FilterChangeHandler<ArticleModel> = (items, query) => {
    const filterQueryString = new URLSearchParams(query).toString()
    
    // Store filter state based on current step
    if (step === WheelSearchStep.Rim) {
      rimFilterState.current = filterQueryString
      setFilterdRims(items)
    } else if (step === WheelSearchStep.Tyre) {
      tyreFilterState.current = filterQueryString
      setFilterdTyres(items)
    }
    
    updateQuery(query, false)
  }

  const updateSelectedRim = useCallback((item: ArticleModel, kTypeId: number) => {
    setSelectedRim(item)
    setStep(WheelSearchStep.Tyre)

    const criteria = {
      articleId: item.id,
      kTypeId: kTypeId,
    }

    setTyresCriteria(criteria)
    triggerTyres(criteria)
  }, [triggerTyres])

  const onSelectArticle = (item: ArticleModel) => {
    if (step === WheelSearchStep.Rim) {
      updateSelectedRim(item, rimResult.data?.carInfo.kTypeId || 0)
    } else if (step === WheelSearchStep.Tyre) {
      if (!selectedRim) {
        throw new Error('No rim selected')
      }

      addPackageToCart({
        articleIds: [selectedRim.id, item.id],
        kTypeId: tyresCriteria?.kTypeId || 0,
        licensePlate: licensePlate || '',
        packageId: stringToNumber(query.pid),
        quantity: 4,
        rimFilterQuery: rimFilterState.current,
        tyreFilterQuery: tyreFilterState.current
      })
    }
  }

  const onChangeRim = () => {
    if (rimCriteria) {
      triggerSearch(rimCriteria, true)
    }
  }

  const data = useMemo(() => {
    if (step === WheelSearchStep.Rim && !rimResult.isError) {
      return filteredRims || rimResult.data?.articles
    } else if (step === WheelSearchStep.Tyre && !tyreResult.isError) {
      return filteredTyres || tyreResult.data
    }
  }, [step, rimResult.isError, rimResult.data?.articles, tyreResult.isError, tyreResult.data, filteredRims, filteredTyres])

  /**
   * Updates the component's state based on the provided query parameters. 
   * The function has multiple conditional paths:
   * - If 'rims' is present in the query, it fetches and sets the selected rim details and fetches the rim's tyres
   * - If 'rims' is not present, it triggers a rim search and sets the step to rim.
   *
   * @param {WheelSearchQueryParams} query - Query parameters object
   */
  const updateStateFromQuery = useCallback(async (query: WheelSearchQueryParams) => {
    if (step === WheelSearchStep.Search && queryLoaded.current)
      return {}

    const newCriteria = {
      licensePlate: query.licenseplate || '',
      licenseCountryCode: query.licensecountrycode || '',
      ktype: query.ktype ? parseInt(query.ktype) : undefined,
    }

    if (query.rims) {
      const articleId = parseInt(query.rims)
      if (articleId) {
        const rim = await triggerArticle({
          id: articleId,
          languageCode: i18n.language,
        })

        if (rim.data) {
          triggerSearch({
            description: '',
            languageCode: i18n.language,
            licensePlate: newCriteria.licensePlate,
            kTypeId: newCriteria.ktype,
            licenseCountryCode: newCriteria.licenseCountryCode
          })
          updateSelectedRim(rim.data, newCriteria.ktype || 0)
        }
      }
    } else if (newCriteria.licensePlate || newCriteria.ktype) {
      triggerSearch({
        description: '',
        languageCode: i18n.language,
        licensePlate: newCriteria.licensePlate,
        kTypeId: newCriteria.ktype,
        licenseCountryCode: newCriteria.licenseCountryCode
      })
    }

    queryLoaded.current = true
  }, [i18n.language, step, triggerArticle, triggerSearch, updateSelectedRim])

  /**
   * When step changes, rebuild query
   */
  useEffect(() => {
    if(prevStep !== WheelSearchStep.Search && step !== WheelSearchStep.Search && prevStep !== step && rimCriteria) 
      rebuildQuery(rimCriteria, true)
  }, [prevStep, rebuildQuery, rimCriteria, step])

  /**
   * Load query from URL and update state
   */
  useEffect(() => {
    if (!queryLoaded.current && router.isReady) {
      updateStateFromQuery(router.query)
    }
  }, [query, queryLoaded, router.isReady, router.query, updateStateFromQuery])

  useEffect(() => {
    const {
      searchplate,
    } = router.query as PageQueryParams

    if (searchplate) {
      triggerLicensePlateSearch()

      // remove searchplate from url
      router.replace(router.pathname, undefined, { shallow: true })
    }

    if (step === WheelSearchStep.Rim && rimCriteria) {
      triggerSearch(rimCriteria)
    }

    if (step === WheelSearchStep.Tyre && tyresCriteria) {
      triggerTyres(tyresCriteria)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const settings = {
    showBrand: true,
    showModel: false,
    showTyreInfo: false,
    showSelectButton: true
  }

  const rimDataResult = rimResult.data?.articles && !rimResult.isFetching && !rimResult.isError
  const tyreDataResult = tyreResult.data && !tyreResult.isFetching && !tyreResult.isError

  const isFetching = rimResult.isFetching || tyreResult.isFetching || !queryLoaded

  return (
    <div className={styles.component}>
      <form className={styles.searchForm} onSubmit={onSubmit}>
        <LicensePlate />
        <div className={styles.searchInput}>
          <Button
            label={t('search.searchButton') || ''}
            rounded={true}
            type="submit"
          />
          <Button
            label={t('common:reset') || ''}
            dark={true}
            rounded={true}
            onClick={resetCriteria}
          />
        </div>
      </form>
      <div className={styles.vehiclePicker}>
        <VehicleSelector
          onSelectVehicle={handleOnSelectVehicle}
          onBrandSelected={() => {}}
          hideSelectors={licensePlateValid || step !== WheelSearchStep.Search || !queryLoaded}
        />
        {
          rimResult.data?.carInfo && !isFetching && (rimDataResult || tyreDataResult) && step !== WheelSearchStep.Search && (
            <VehicleInfo
              info={rimResult.data?.carInfo}
            />
          )
        }
      </div>
      {step >= WheelSearchStep.Rim && (
        <div className={styles.stepContainer}>
          <div className={styles.header}>
            <h3>{t('wheels:step')} 0{step}</h3>
            <span>
              {step === WheelSearchStep.Rim && t('wheels:stepRim')}
              {step === WheelSearchStep.Tyre && t('wheels:stepTyre')}
            </span>
          </div>
          {selectedRim && (
            <div className={styles.selectedProduct}>
              <ProductCardSimple
                headerText={t('wheels:selectedRim')}
                item={selectedRim}
                buttonLabel={t('wheels:changeRim')}
                onClick={onChangeRim}
              />
            </div>
          )}
        </div>
      )}
      <div className={styles.filter}>
        {rimDataResult && (step === WheelSearchStep.Rim) && (
          <RimFilter
            items={rimResult.data?.articles || []}
            onFilterChange={onFilterChange}
          />
        )}
        {tyreDataResult && (step === WheelSearchStep.Tyre) && (
          <WheelTyreFilter
            items={tyreResult.data || []}
            onFilterChange={onFilterChange}
          />
        )}
      </div>
      {rimResult.isError && (
        <NoCarFound
          onClickCarPicker={resetCriteria}
        />
      )}
      <SearchResult
        isFetching={isFetching}
        items={data}
        settings={settings}
        onSelect={onSelectArticle}
        listId={'search_wheels'}
        listName={'Search wheels'}
        metaData={{
          kType: rimResult.data?.carInfo.kTypeId || 0,
          licensePlate: licensePlate || '',
        }}
        step={step}
      />
    </div>
  )
}

export default WheelSearch